import React, { useState, useEffect } from 'react';

import { find } from 'lodash';

import { FILTER_OPTIONS, FILTER_OPTION_TO_JSON, Action } from '../constants';
import { useAccountListContext } from '../context';
import { FilterOptionType } from '../types';

type FilterOptionsProps = {
  isFilterOpen: boolean;
};

const STEPS = {
  SELECTING_OPTION: 1,
  SELECTING_OPTION_VALUE: 2,
};

const FilterOptions: React.FC<FilterOptionsProps> = ({ isFilterOpen }: FilterOptionsProps) => {
  // Step inside filter menu
  const [step, setStep] = useState(STEPS.SELECTING_OPTION);
  // string value of the selected filter
  const [filterOption, setFilterOption] = useState<string>('');
  // selected options value inside each filter option
  const [selectedOptionValues, setSelectedOptionValues] = useState<{
    type: string;
    options: FilterOptionType[];
  }>();
  const { dispatch } = useAccountListContext();
  const selectedOption = FILTER_OPTION_TO_JSON[filterOption];

  const handleSetFilterOption = (e: React.MouseEvent<HTMLElement>): void => {
    const option = e.currentTarget.getAttribute('data-value') || '';
    setFilterOption(option);
    setStep(STEPS.SELECTING_OPTION_VALUE);
  };

  // Used for when is a boolean option
  const handleSetSelectedBoolean = (e: React.ChangeEvent<HTMLInputElement>): void => {
    // boolean always has only one option
    setSelectedOptionValues({
      type: 'boolean',
      options: [{ label: filterOption, value: e.target.value }],
    });
  };

  // Used for when is a select option
  const handleApplyFilter = (): void => {
    // if no filter is selected or no options then do not apply filter
    if (!filterOption || !selectedOptionValues || selectedOptionValues?.options.length === 0)
      return;
    dispatch({
      type: Action.SET_FILTERS,
      filters: { [filterOption]: selectedOptionValues },
    });
  };

  const handleAddOrRemoveOption = (option: FilterOptionType) => (): void => {
    // if included then remove it from the array
    if (find(selectedOptionValues?.options, option)) {
      setSelectedOptionValues((prevState) => {
        if (!prevState || !selectedOptionValues) return prevState;
        return {
          ...prevState,
          options: selectedOptionValues.options.filter((opt) => opt.value !== option.value),
        };
      });
      return;
    }
    setSelectedOptionValues((prevState) => {
      // if no options before put first one
      if (!prevState || !selectedOptionValues) return { type: 'select', options: [option] };
      return { ...prevState, options: [...selectedOptionValues.options, option] };
    });
  };

  // Check if what options are check with filter option type === selected
  const isChecked = (optionValue: string): boolean => {
    if (!selectedOptionValues) return false;
    const index = selectedOptionValues.options.findIndex((opt) => opt.value === optionValue);
    return index >= 0;
  };

  // If menu is open/closed reset step to the first step
  useEffect(() => {
    setStep(STEPS.SELECTING_OPTION);
  }, [isFilterOpen]);

  return (
    <div
      className="absolute right-0 md:left-0 bg-white-0.0 min-w-80 mt-2 origin-top-right rounded shadow-lg z-50 border border-white-1.0"
      hidden={!isFilterOpen}
    >
      <div className="flex">
        {step === STEPS.SELECTING_OPTION_VALUE && (
          <button type="button" className="ml-4" onClick={() => setStep(STEPS.SELECTING_OPTION)}>
            <i className="icon-chevron_left_icon text-gray-1.0" />
          </button>
        )}
        <p className="font-semibold px-4 py-1 text-gray-1.0">Filter properties</p>
      </div>
      {step === STEPS.SELECTING_OPTION && (
        <div
          className="py-1"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
        >
          {FILTER_OPTIONS.map((option) => (
            <div
              className="hover:bg-white-1.0 focus:outline-none block px-4 py-2 text-sm cursor-pointer"
              role="presentation"
              onClick={handleSetFilterOption}
              key={option.value}
              data-value={option.value}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
      {step === STEPS.SELECTING_OPTION_VALUE && (
        <div
          className="flex flex-col"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
        >
          {selectedOption.type === 'select' && (
            <>
              {selectedOption.options?.map((option) => (
                <label
                  htmlFor={option.label}
                  className="inline-flex items-center px-4 py-2 hover:bg-white-1.0 hover:text-primary-3.0"
                  key={option.value}
                >
                  <input
                    onChange={handleAddOrRemoveOption(option)}
                    id={option.label}
                    type="checkbox"
                    className="w-5 h-5"
                    checked={isChecked(option.value)}
                  />
                  <span className="ml-2">{option.label}</span>
                </label>
              ))}
            </>
          )}
          {selectedOption.type === 'boolean' && (
            <>
              <label
                htmlFor="true-option"
                className="inline-flex items-center px-4 py-2 hover:bg-white-1.0 hover:text-primary-3.0"
              >
                <input
                  type="radio"
                  id="true-option"
                  name="boolean-option"
                  value="true"
                  onChange={handleSetSelectedBoolean}
                />
                <span className="ml-2">True</span>
              </label>
              <label
                htmlFor="false-option"
                className="inline-flex items-center px-4 py-2 hover:bg-white-1.0 hover:text-primary-3.0"
              >
                <input
                  type="radio"
                  id="false-option"
                  name="boolean-option"
                  value="false"
                  onChange={handleSetSelectedBoolean}
                />
                <span className="ml-2">False</span>
              </label>
            </>
          )}
          <button
            className="flex btn bg-primary-3.0 py-3 mt-2"
            type="button"
            onClick={handleApplyFilter}
            disabled={!selectedOptionValues || selectedOptionValues.options.length === 0}
          >
            <p className="text-white-0.0 m-auto font-semibold">Apply filter</p>
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterOptions;
